import { api } from "@/redux/api";

export interface DefaultParticipant {
	id: number;
	firstName: string;
	lastName: string;
	internalRoles: string;
	scenarioRoles: string;
	lastUsedAt: number;
}

export interface CreateDefaultParticipantBody {
	firstName: string;
	lastName: string;
	internalRoles: string;
	scenarioRoles: JSON;
}

export interface UpdateDefaultParticipantBody {
	firstName?: string;
	lastName?: string;
	internalRoles?: string;
	scenarioRoles?: JSON;
}

/**
 * Default participants belong to a company. Callers that omit `companyId` act on
 * their own company; passing one addresses that company, which the API only
 * allows a global admin to do.
 */
const withCompany = (url: string, companyId?: number) =>
	companyId ? `${url}${url.includes("?") ? "&" : "?"}companyId=${companyId}` : url;

const OWN_COMPANY = "own";

const listTag = (companyId?: number | void) => ({
	type: "DefaultParticipants" as const,
	id: companyId ?? OWN_COMPANY,
});

/**
 * A change touches the list of the company it was made for *and* the caller's own list:
 * the company page names the caller's own company by id while the Start Scenario dialog
 * asks without one, so both tags describe the same list and both must refresh (BCM360-341).
 */
const changedTags = (companyId?: number) =>
	companyId ? [listTag(companyId), listTag()] : [listTag()];

export const defaultParticipantsApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getDefaultParticipants: builder.query<{ data: DefaultParticipant[] }, number | void>({
			query: (companyId) => ({ url: withCompany("default-participants", companyId ?? undefined) }),
			providesTags: (_result, _error, companyId) => [listTag(companyId)],
		}),
		createDefaultParticipant: builder.mutation<
			DefaultParticipant,
			CreateDefaultParticipantBody & { companyId?: number }
		>({
			query: ({ companyId, ...body }) => ({
				url: withCompany("default-participants", companyId),
				method: "POST",
				body,
			}),
			invalidatesTags: (_result, _error, { companyId }) => changedTags(companyId),
		}),
		updateDefaultParticipant: builder.mutation<
			DefaultParticipant,
			{ id: number; body: UpdateDefaultParticipantBody; companyId?: number }
		>({
			query: ({ id, body, companyId }) => ({
				url: withCompany(`default-participants/${id}`, companyId),
				method: "PATCH",
				body,
			}),
			invalidatesTags: (_result, _error, { companyId }) => changedTags(companyId),
		}),
		deleteDefaultParticipant: builder.mutation<
			{ participantId: number },
			{ id: number; companyId?: number }
		>({
			query: ({ id, companyId }) => ({
				url: withCompany(`default-participants/${id}`, companyId),
				method: "DELETE",
			}),
			invalidatesTags: (_result, _error, { companyId }) => changedTags(companyId),
		}),
	}),
});

export const {
	useGetDefaultParticipantsQuery,
	useCreateDefaultParticipantMutation,
	useUpdateDefaultParticipantMutation,
	useDeleteDefaultParticipantMutation,
} = defaultParticipantsApi;
